import Loader from '@components/widgets/Loader';
import { DETAILS_BANK, DETAILS_VIEW_BANK } from '@constants';
import { GET_INSPECTION_FILE, post } from '@services';
import { useInspectionDetailStore } from '@stores';
import _get from 'lodash/get';
import _map from 'lodash/map';
import {
  DocumentFormType,
  InspectionSecurityStatusEnum,
  BankSecurityCheckEnum
} from '@types';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formatName } from '@utils/formatters';
import EnlargeableImage from '@components/widgets/EnlargeableImage';
interface IProps {
  type: DocumentFormType;
  status: InspectionSecurityStatusEnum;
  data: any;
}

const displayedAddressLine = (field?: {
  userEditValue?: string | null;
  ocrValue?: string | null;
}) => (field?.userEditValue ? field.userEditValue : field?.ocrValue);

const BankCheckCard = ({ type, data }: IProps) => {
  const { activePanel, isCollapsed, inspectionDetailData } =
    useInspectionDetailStore();

  const isDemo = location.pathname.includes('/demo/inspection');
  const formattedDate = (date: any) => format(date, 'dd/MM/yyyy h:mma');
  const [formElements, setFormElements] = useState<any>([]);
  const [isLoading, setLoading] = useState(false);
  const [imgsrc, setImgSrc] = useState<any>();
  const param = useParams();
  const [selectedFile, setSelectedFile] = useState<any>();
  const [ocrData, setOcrData] = useState<any>();
  const [seller, setSeller] = useState<any>([]);
  const [specialFormElements, setSpecialFormElements] = useState<any>([]);
  useEffect(() => {
    const shouldLoadBankImage =
      (type === BankSecurityCheckEnum.BANK_SECURITY_DATA_EDIT && !isDemo) ||
      type === BankSecurityCheckEnum.BANK_SECURITY_AUTHENTICITY;
    if (shouldLoadBankImage) {
      const fileTypes = (inspectionDetailData.files ?? []).filter(
        (e: any) => e.documentType === 'BANK_STATEMENTS'
      );
      setOcrData(inspectionDetailData.inspectionChecks.bankStatement);
      if (fileTypes.length > 0) {
        setSeller(fileTypes[0].user);
        setLoading(true);
        post(
          GET_INSPECTION_FILE,
          { inspectionId: param.id, fileId: fileTypes[0].fileId },
          false
        )
          .then((data) => {
            setLoading(false);
            if (data && data.dataBase64) {
              setImgSrc(data.dataBase64);
              setSelectedFile(data.file);
            }
          })
          .catch((e) => {
            setLoading(false);
            console.error(e);
          });
      }
    }
    setSpecialFormElements(DETAILS_VIEW_BANK);
    setFormElements(DETAILS_BANK);
  }, [type, inspectionDetailData.files]);
  if (isLoading) return <Loader />;
  switch (type) {
    case BankSecurityCheckEnum.BANK_SECURITY_DATA_EDIT:
      return (
        <div className="flex flex-col p-6 gap-6 border-t">
          <div className="flex flex-col gap-x-2 h-full min-h-0">
            <div className="flex flex-col w-full gap-y-2">
              <p className="font-medium text-grey-700 text-base bg-warning-50 p-4 rounded-md">
                Seller-submitted data differs from OCR-extracted data. This may
                be due to an OCR misread or manual input where OCR retrieval was
                incomplete. Compare the extracted data with the seller&apos;s entry
                and the document image to verify accuracy.
              </p>

              <h1 className="text-grey-800 font-semibold text-base">
                Provided by{' '}
                {seller
                  ? formatName(seller.firstName, seller.lastName)
                  : formatName(inspectionDetailData.seller.firstName, inspectionDetailData.seller.lastName)}
              </h1>

              <div className="flex flex-col gap-8">
                {imgsrc && !isDemo && (
                  <EnlargeableImage
                    src={`data:image/jpeg;base64,${imgsrc}`}
                    alt="Bank statement"
                  />
                )}
                {isDemo && (
                  <EnlargeableImage src={imgsrc} alt="Bank statement" />
                )}
              </div>
              <div className="flex flex-col justify-center items-center">
                {formElements.map((row: any, index: number) => {
                  if (row.key === 'location') {
                    const hasClientAddress = !!_get(
                      selectedFile,
                      'clientAddress'
                    );
                    const hasNetworkAddress = !!_get(
                      selectedFile,
                      'networkAddress'
                    );
                    if (hasClientAddress) {
                      return (
                        <div key={index} className="flex flex-row gap-x-1">
                          <p className="text-grey-500 text-xs leading-[18px]">
                            {row.displayName + ':'}
                          </p>
                          <p className="text-grey-500 text-xs leading-[18px]">
                            {_get(selectedFile, 'clientAddress.label')}
                          </p>
                        </div>
                      );
                    }
                    if (hasNetworkAddress) {
                      return (
                        <div
                          key={index}
                          className="w-full flex flex-col items-center">
                          <p className="text-grey-500 text-xs leading-[18px] text-center">
                            Approximate location based on IP address.
                          </p>
                          <div className="flex flex-row gap-x-1 justify-center">
                            <p className="text-grey-500 text-xs leading-[18px]">
                              {row.displayName + ':'}
                            </p>
                            <p className="text-grey-500 text-xs leading-[18px]">
                              {_get(selectedFile, 'networkAddress.label')}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={index}
                        className="w-full flex flex-col items-center">
                        <p className="text-grey-500 text-xs leading-[18px] text-center">
                          Location unavailable.
                        </p>
                      </div>
                    );
                  }
                  if (_get(selectedFile, row.keyPath)) {
                    return (
                      <div key={index} className="flex flex-row gap-x-1">
                        <p className="text-grey-500 text-xs leading-[18px]">
                          {row.displayName + ':'}
                        </p>
                        <p className="text-grey-500 text-xs leading-[18px]">
                          {row.type === 'Date'
                            ? formattedDate(_get(selectedFile, row.keyPath))
                            : _get(selectedFile, row.keyPath)}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
              {specialFormElements.length > 0 && (
                <div className="flex flex-col justify-center items-center gap-6 mt-6">
                  <p className="font-semibold text-grey-800 w-full border-b border-grey-200 pb-3.5">
                    Seller submitted details
                  </p>
                  {_map(specialFormElements, (row: any, index: number) => (
                    <React.Fragment key={index}>
                      <div className="flex flex-col border-b border-grey-200 w-full gap-3">
                        {_get(ocrData, row.keyPath) && (
                          <div className="flex flex-row justify-between py-3.5 mt-[-8px]">
                            <p className="text-grey-500 text-sm leading-5">
                              {row.displayName}
                            </p>
                            <p className="text-base text-grey-700">
                              {_get(ocrData, row.keyPath)?.userEditValue}
                            </p>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                  <p className="font-semibold text-grey-800 w-full  border-b border-grey-200 pb-3.5">
                    OCR-extracted data
                  </p>
                  {_map(specialFormElements, (row: any, index: number) => (
                    <React.Fragment key={index}>
                      <div className="flex flex-col border-b  border-grey-200 w-full gap-3">
                        {_get(ocrData, row.keyPath) && (
                          <div className="flex flex-row justify-between py-3.5 mt-[-8px]">
                            <p className="text-grey-500 text-sm leading-5">
                              {row.displayName}
                            </p>
                            <p className="text-base text-grey-700">
                              {_get(ocrData, row.keyPath)?.ocrValue}
                            </p>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}
              {!isCollapsed && (
                <div className="w-full bg-white flex flex-col md:flex-row gap-4 mt-6 mb-[60px]">
                </div>
              )}
            </div>
          </div>
        </div>
      );
    case BankSecurityCheckEnum.BANK_SECURITY_AUTHENTICITY:
      return (
        <div className="flex flex-col p-6 gap-6 border-t">
          <div className="flex flex-col gap-x-2 h-full min-h-0">
            <div className="flex flex-col w-full gap-y-2">
              <p className="font-medium text-grey-700 text-base bg-warning-50 p-4 rounded-md">
                Unable to verify with high confidence that the Bank Statement
                provided is authentic.
              </p>
              {imgsrc && (
                <EnlargeableImage
                  src={`data:image/jpeg;base64,${imgsrc}`}
                  alt="Bank statement"
                />
              )}
              {!isCollapsed && (
                <div className="w-full bg-white flex flex-col md:flex-row gap-4 mt-6 mb-[60px]">
                </div>
              )}
            </div>
          </div>
        </div>
      );
    case BankSecurityCheckEnum.BANK_STATEMENT_ADDRESS: {
      const {bankStatement, regoCertificate} = inspectionDetailData.inspectionChecks;
      return (
        <div className="flex flex-col p-6 gap-6 border-t">
          <div className="flex flex-col gap-x-2 h-full min-h-0">
            <div className="flex flex-col w-full gap-y-2">
              <p className="font-medium text-grey-700 text-base bg-warning-50 p-4 rounded-md">
                The address on the bank statement does not match the address on
                the registration certificate.
              </p>
              <div className="flex flex-col border-none w-full">
                <p className="font-semibold border-b border-grey-200 text-grey-800  py-3.5">
                  Registration certificate
                </p>
                <div className="grid grid-cols-2  py-3.5 border-b border-grey-200">
                  <p className="text-grey-500 text-sm font-medium leading-4">
                    Address Line1
                  </p>
                  <p className="text-sm font-medium text-grey-950  text-right">
                    {displayedAddressLine(regoCertificate?.addressLine1)}
                  </p>
                </div>
                <div className="grid grid-cols-2  py-3.5 border-b border-grey-200">
                  <p className="text-grey-500 text-sm font-medium leading-4">
                    Address Line2
                  </p>
                  <p className="text-sm font-medium text-grey-950  text-right">
                    {displayedAddressLine(regoCertificate?.addressLine2)}
                  </p>
                </div>
              </div>
              <div className="flex flex-col border-none w-full">
                <p className="font-semibold border-b border-grey-200 text-grey-800  py-3.5">
                  Bank statement
                </p>
                <div className="grid grid-cols-2  py-3.5 border-b border-grey-200">
                  <p className="text-grey-500 text-sm font-medium leading-4">
                    Address Line1
                  </p>
                  <p className="text-sm font-medium text-grey-950  text-right">
                    {displayedAddressLine(bankStatement?.addressLine1)}
                  </p>
                </div>
                <div className="grid grid-cols-2  py-3.5 border-b border-grey-200">
                  <p className="text-grey-500 text-sm font-medium leading-4">
                    Address Line2
                  </p>
                  <p className="text-sm font-medium text-grey-950  text-right">
                    {displayedAddressLine(bankStatement?.addressLine2)}
                  </p>
                </div>
              </div>
              {!isCollapsed && (
                <div className="w-full bg-white flex flex-col md:flex-row gap-4 mt-6 mb-[60px]">
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    default:
      return <> </>;
  }
};
export default BankCheckCard;
